import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import AuthModal from "@/components/AuthModal";
import { 
  Globe, 
  Zap, 
  Shield, 
  BarChart3, 
  BookOpen, 
  Clock,
  ArrowRight,
  Languages,
  Lock,
  Cpu,
  LogOut,
  User
} from "lucide-react";

const LandingPage = () => {
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const navigate = useNavigate();

  const handleProtectedAction = () => {
    if (isAuthenticated) {
      navigate("/translate");
    } else {
      setShowAuthModal(true);
    }
  };

  const handleAuthSuccess = () => {
    navigate("/translate");
  };

  const features = [
    {
      icon: Languages,
      title: "Bi-Directional Translation",
      description: "Seamless EN-CA ↔ FR-CA translation with equal quality in both directions",
      color: "text-primary"
    },
    {
      icon: Lock,
      title: "Terminology Lock",
      description: "110+ critical meteorological terms guaranteed accurate, always",
      color: "text-emerald-600"
    },
    {
      icon: Zap,
      title: "Sub-500ms Latency",
      description: "Real-time translation for 24/7 weather operations",
      color: "text-secondary"
    },
    {
      icon: Cpu,
      title: "Hybrid AI Architecture",
      description: "Smart routing between GPT-5.2, Claude & Gemini for optimal results",
      color: "text-cyan-600"
    },
    {
      icon: Shield,
      title: "Government-Ready",
      description: "Full audit trail, confidence scoring, and compliance features",
      color: "text-primary"
    },
    {
      icon: BarChart3,
      title: "BLEU/COMET Scoring",
      description: "Objective quality metrics aligned with RFI requirements",
      color: "text-emerald-600"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Globe className="w-6 h-6 text-primary" />
            <span className="font-heading text-xl font-bold tracking-tight text-foreground">MeteoTranslate</span>
          </Link>
          
          {/* Navigation links - only show when authenticated */}
          {isAuthenticated && (
            <div className="hidden md:flex items-center gap-8">
              <Link to="/translate" className="nav-link text-sm font-medium" data-testid="nav-translate">
                Translate
              </Link>
              <Link to="/terminology" className="nav-link text-sm font-medium" data-testid="nav-terminology">
                Terminology
              </Link>
              <Link to="/metrics" className="nav-link text-sm font-medium" data-testid="nav-metrics">
                Metrics
              </Link>
              <Link to="/analytics" className="nav-link text-sm font-medium" data-testid="nav-analytics">
                Analytics
              </Link>
              <Link to="/history" className="nav-link text-sm font-medium" data-testid="nav-history">
                History
              </Link>
              <Link to="/settings" className="nav-link text-sm font-medium" data-testid="nav-settings">
                Settings
              </Link>
              {isAdmin && (
                <Link to="/admin" className="nav-link text-sm font-medium text-primary" data-testid="nav-admin">
                  Admin
                </Link>
              )}
            </div>
          )}
          
          {isAuthenticated ? (
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
                <User className="w-4 h-4" />
                <span>{user?.name}</span>
                {isAdmin && (
                  <span className="px-1.5 py-0.5 bg-primary/10 text-primary text-xs rounded-full font-medium">
                    Admin
                  </span>
                )}
              </div>
              <Button 
                variant="outline" 
                onClick={async () => {
                  await logout();
                  navigate("/signed-out");
                }}
                className="border-border"
                data-testid="nav-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          ) : (
            <Button 
              onClick={handleProtectedAction}
              className="bg-primary hover:bg-primary-hover text-white shadow-glow"
              data-testid="nav-get-started-btn"
            >
              Get Started
            </Button>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-bg pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-sm text-primary font-medium">RFI #5000088030 Demo Solution</span>
            </div>
            
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight mb-6 text-foreground" data-testid="hero-title">
              AI-Powered
              <br />
              <span className="text-primary">Meteorological</span>
              <br />
              Translation
            </h1>
            
            <p className="text-lg text-muted-foreground max-w-2xl mb-8" data-testid="hero-description">
              Automated machine-to-machine translation for Environment and Climate Change Canada. 
              Bi-directional EN-CA ↔ FR-CA with specialized weather terminology.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary-hover text-white shadow-glow-lg h-12 px-8"
                onClick={handleProtectedAction}
                data-testid="hero-try-now-btn"
              >
                Try Translation Now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Link to="/terminology" data-testid="hero-view-terminology-btn">
                <Button size="lg" variant="outline" className="h-12 px-8 border-border hover:bg-muted">
                  View Terminology
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16">
            {[
              { value: "110+", label: "Locked Terms" },
              { value: "<500ms", label: "Avg Latency" },
              { value: "24/7", label: "Availability" },
              { value: "3", label: "AI Models" },
            ].map((stat, i) => (
              <div key={i} className="stats-card text-center" data-testid={`stat-${i}`}>
                <div className="font-heading text-3xl font-black text-primary mb-1">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6 bg-muted">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4 text-foreground" data-testid="features-title">
              Built for Meteorological Precision
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Designed to meet ECCC requirements with enterprise-grade reliability and accuracy.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div 
                key={i} 
                className="metric-card p-6 bg-white border border-border rounded-lg hover:shadow-card-lg transition-all"
                data-testid={`feature-card-${i}`}
              >
                <feature.icon className={`w-10 h-10 ${feature.color} mb-4`} />
                <h3 className="font-heading text-xl font-bold mb-2 text-foreground">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4 text-foreground" data-testid="how-it-works-title">
              How It Works
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                icon: BookOpen,
                title: "Input Weather Alert",
                description: "Submit meteorological text in EN-CA or FR-CA via API or interface"
              },
              {
                step: "02",
                icon: Cpu,
                title: "AI Processing",
                description: "Hybrid AI with terminology locking ensures precise translations"
              },
              {
                step: "03",
                icon: Clock,
                title: "Instant Results",
                description: "Receive translation with confidence score and audit trail"
              }
            ].map((item, i) => (
              <div key={i} className="relative" data-testid={`step-${i}`}>
                <div className="text-6xl font-heading font-black text-muted/50 absolute -top-4 -left-2">
                  {item.step}
                </div>
                <div className="relative z-10 pt-8">
                  <item.icon className="w-8 h-8 text-primary mb-4" />
                  <h3 className="font-heading text-xl font-bold mb-2 text-foreground">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-muted border-y border-border">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4 text-foreground" data-testid="cta-title">
            Ready to Experience the Demo?
          </h2>
          <p className="text-muted-foreground mb-8">
            Test our meteorological translation system with real weather alerts.
          </p>
          <Button 
            size="lg" 
            className="bg-primary hover:bg-primary-hover text-white shadow-glow-lg h-12 px-8"
            onClick={handleProtectedAction}
            data-testid="cta-start-btn"
          >
            Start Translating
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-8">
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              <span className="font-heading text-lg font-bold text-foreground">MeteoTranslate</span>
            </div>
            <div className="text-sm text-muted-foreground text-center">
              Demo Solution for RFI #5000088030 — Environment and Climate Change Canada
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/terminology" className="text-muted-foreground hover:text-foreground transition-colors">
                Terminology
              </Link>
              <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
          
          {/* ALTAM Branding */}
          <div className="pt-6 border-t border-border text-center">
            <p className="text-sm text-muted-foreground mb-2">
              Powered by <span className="font-semibold text-foreground">ALTAM™ Operating Software</span>
            </p>
            <p className="text-xs text-muted-foreground">
              ALTAM™ is a product of Pecaro Consulting Inc. © {new Date().getFullYear()} All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
    </div>
  );
};

export default LandingPage;
